import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ConfirmDialog } from "../../components/Dialog";
import { Alert, Button, TextArea } from "../../components/ui";
import { useToast } from "../../components/Toast";
import { api, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { formatDateTime } from "../../lib/format";
import type { OrderDetail } from "./types";

/** Customer action: answer a due date Bill's proposed. */
export function CustomerOrderActions({ order }: { order: OrderDetail }) {
  const qc = useQueryClient();
  const { notify } = useToast();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const [answer, setAnswer] = useState<"accept" | "reject" | null>(null);
  const [answerMessage, setAnswerMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const proposal = order.due_proposals.find((p) => p.state === "open");

  const refresh = (data: OrderDetail) => qc.setQueryData(["order", order.id], data);

  const respond = async () => {
    if (!proposal || !answer) return;
    setBusy(true);
    try {
      const data = await api.post<OrderDetail>(
        `/orders/${order.id}/due-proposals/${proposal.id}/${answer}`,
        {
          message: answerMessage || null,
        },
      );
      refresh(data);
      notify(
        answer === "accept"
          ? "Thank you. The new due date is confirmed."
          : "Thank you. Bill's will contact you.",
      );
    } catch (err) {
      notify(errorMessage(err), "error");
      await qc.invalidateQueries({ queryKey: ["order", order.id] });
    } finally {
      setBusy(false);
      setAnswer(null);
    }
  };

  return (
    <>
      {proposal && (
        <Alert tone="warning" title="Bill's proposed a new due date">
          <p>
            Bill's cannot meet the date you requested. Proposed:{" "}
            <strong>{formatDateTime(proposal.proposed_due_at, tz)}</strong> (instead of{" "}
            {formatDateTime(proposal.previous_due_at, tz)}).
          </p>
          {proposal.message && <p className="mt-1">Message from Bill's: {proposal.message}</p>}
          <div className="mt-3 max-w-lg">
            <TextArea
              label="Message to Bill's"
              rows={2}
              value={answerMessage}
              onChange={(e) => setAnswerMessage(e.target.value)}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => setAnswer("accept")}>Accept new date</Button>
            <Button variant="secondary" onClick={() => setAnswer("reject")}>
              Decline
            </Button>
          </div>
        </Alert>
      )}
      <ConfirmDialog
        open={answer !== null}
        title={answer === "accept" ? "Accept the new due date?" : "Decline the new due date?"}
        body={
          answer === "accept"
            ? `Your order will be due ${proposal ? formatDateTime(proposal.proposed_due_at, tz) : ""}.`
            : "Bill's will be told you cannot accept this date and will contact you."
        }
        confirmLabel={answer === "accept" ? "Accept" : "Decline"}
        busy={busy}
        onConfirm={respond}
        onCancel={() => setAnswer(null)}
      />
    </>
  );
}
